import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "./QuizPage.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const QuizPage = () => {
  const { quizId: urlQuizId } = useParams(); // Get quiz ID from URL params
  const quizIdToUse = urlQuizId || "default-quiz"; // Fallback if no ID provided

  // Registration state
  const [isRegistered, setIsRegistered] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: "",
    email: "",
    phone: "",
    class: "",
    college: "",
    location: "",
  });
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [existingQuiz, setExistingQuiz] = useState(null); // For resume functionality

  // Tab switching detection
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showTabWarning, setShowTabWarning] = useState(false);

  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [activeQuizId, setActiveQuizId] = useState(null); // Store active quiz ID from backend
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [startTime, setStartTime] = useState(null);

  // Result state
  const [result, setResult] = useState(null);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "10:00"; // Default fallback
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    // Ensure both minutes and seconds are always 2 digits
    const minsStr = mins.toString().padStart(2, "0");
    const secsStr = secs.toString().padStart(2, "0");
    return `${minsStr}:${secsStr}`;
  };

  // Get score message styling
  const getScoreStyle = (level) => {
    switch (level) {
      case "excellent":
        return "score-excellent";
      case "good":
        return "score-good";
      case "average":
        return "score-average";
      case "below_average":
        return "score-below-average";
      case "poor":
        return "score-poor";
      default:
        return "";
    }
  };

  // Handle registration input change
  const handleRegistrationChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Check if email already registered
  const checkExistingRegistration = async (email) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz-registration/registrations/${quizIdToUse}`,
      );
      const data = await response.json();

      if (response.ok) {
        const existing = data.registrations.find((reg) => reg.email === email);
        if (existing) {
          setExistingQuiz(existing);
          return existing;
        }
      }
      return null;
    } catch (error) {
      console.error("Error checking existing registration:", error);
      return null;
    }
  };

  // Register for quiz
  const registerHandler = async (e) => {
    e.preventDefault();
    setRegistrationLoading(true);
    setError(null);

    try {
      // Check if email already registered
      const existing = await checkExistingRegistration(registrationData.email);

      if (existing) {
        if (existing.quizSubmitted) {
          throw new Error(
            "You have already completed this quiz. You cannot retake it.",
          );
        }
        // Resume existing quiz
        setIsRegistered(true);
        setExistingQuiz(existing);
        // Load the existing quiz session
        resumeQuiz(existing);
        return;
      }

      // New registration
      const response = await fetch(
        `${API_BASE_URL}/api/quiz-registration/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            quizId: quizIdToUse,
            ...registrationData,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Registration failed");
      }

      setIsRegistered(true);
      // Auto-start quiz after registration
      startQuizHandler();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegistrationLoading(false);
    }
  };

  // Start quiz
  const startQuizHandler = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/start-quiz/${quizIdToUse}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();
      console.log("Quiz start response:", data); // Debug log

      if (!response.ok) {
        throw new Error(data.message || "Failed to start quiz");
      }

      setQuiz(data);
      setActiveQuizId(data.quiz_id); // Store quiz ID
      setCurrentQuestion(data.question);
      setQuestionNumber(data.questionNumber);
      setTotalQuestions(data.totalQuestions);
      setTimeRemaining(Number(data.timeRemaining) || 600); // Ensure it's a number with fallback
      setStartTime(new Date(data.startTime));
      setQuizStarted(true);
      console.log("Time remaining set to:", data.timeRemaining); // Debug log
      window.scrollTo(0, 0); // Scroll to top when quiz starts
    } catch (err) {
      setError(err.message);
      console.error("Start quiz error:", err); // Debug log
    } finally {
      setLoading(false);
    }
  }, [quizIdToUse]);

  // Resume existing quiz
  const resumeQuiz = async (existingRegistration) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch the quiz details from backend
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/start-quiz/${quizIdToUse}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to resume quiz");
      }

      setQuiz(data);
      setActiveQuizId(data.quiz_id);
      setCurrentQuestion(data.question);
      setQuestionNumber(data.questionNumber);
      setTotalQuestions(data.totalQuestions);
      setTimeRemaining(Number(data.timeRemaining) || 600);
      setStartTime(new Date(data.startTime));
      setQuizStarted(true);
      window.scrollTo(0, 0); // Scroll to top when quiz resumes
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Tab switching detection - Warning on 1st, Auto-submit on 2nd
  useEffect(() => {
    if (!quizStarted || quizSubmitted) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setTabSwitchCount((prev) => {
          const newCount = prev + 1;

          if (newCount === 1) {
            // First time - show warning
            setShowTabWarning(true);
            setTimeout(() => setShowTabWarning(false), 5000);
          } else if (newCount >= 2) {
            // Second time - auto submit immediately
            setShowTabWarning(false);
            fetch(`${API_BASE_URL}/api/quiz/submit-quiz/${activeQuizId}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
            })
              .then((res) => res.json())
              .then((data) => {
                setResult(data);
                setQuizSubmitted(true);

                // Save score to registration
                if (registrationData.email) {
                  fetch(`${API_BASE_URL}/api/quiz-registration/update-score`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      quizId: quizIdToUse,
                      email: registrationData.email,
                      score: data.score,
                    }),
                  }).catch((err) =>
                    console.error("Failed to save score:", err),
                  );
                }
              })
              .catch((err) => {
                console.error("Auto-submit error:", err);
                setError("Quiz auto-submitted due to tab switching violation");
                setQuizSubmitted(true);
              });
          }

          return newCount;
        });
      }
    };

    // Also detect window blur (screenshot tool or other app)
    const handleBlur = () => {
      setTimeout(() => {
        if (!document.hidden && quizStarted && !quizSubmitted) {
          setTabSwitchCount((prev) => {
            const newCount = prev + 1;

            if (newCount === 1) {
              // First time - show warning
              setShowTabWarning(true);
              setTimeout(() => setShowTabWarning(false), 5000);
            } else if (newCount >= 2) {
              // Second time - auto submit
              setShowTabWarning(false);
              fetch(`${API_BASE_URL}/api/quiz/submit-quiz/${activeQuizId}`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
              })
                .then((res) => res.json())
                .then((data) => {
                  setResult(data);
                  setQuizSubmitted(true);

                  if (registrationData.email) {
                    fetch(
                      `${API_BASE_URL}/api/quiz-registration/update-score`,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                          quizId: quizIdToUse,
                          email: registrationData.email,
                          score: data.score,
                        }),
                      },
                    ).catch((err) =>
                      console.error("Failed to save score:", err),
                    );
                  }
                })
                .catch((err) => {
                  console.error("Auto-submit error:", err);
                  setError("Quiz auto-submitted");
                  setQuizSubmitted(true);
                });
            }

            return newCount;
          });
        }
      }, 200);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, [
    quizStarted,
    quizSubmitted,
    activeQuizId,
    registrationData.email,
    quizIdToUse,
  ]);

  // Request fullscreen when quiz starts - makes it harder to switch apps
  useEffect(() => {
    if (!quizStarted || quizSubmitted) return;

    const requestFullscreen = () => {
      const elem = document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen().catch(() => {});
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen().catch(() => {});
      }
    };

    // Request fullscreen after a short delay
    const timeoutId = setTimeout(requestFullscreen, 500);

    return () => {
      clearTimeout(timeoutId);
      // Exit fullscreen when quiz ends
      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [quizStarted, quizSubmitted]);

  // Submit answer
  const submitAnswerHandler = async () => {
    if (!activeQuizId) {
      setError("Quiz ID not found");
      return;
    }
    if (!selectedAnswer || isAnswered) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/submit-answer/${activeQuizId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question_id: currentQuestion._id,
            selectedAnswer,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit answer");
      }

      // Check if quiz was submitted (all questions answered or time expired)
      if (data.score !== undefined || data.isTimeExpired) {
        setResult(data);
        setQuizSubmitted(true);
      } else {
        // Move to next question
        setCurrentQuestion(data.nextQuestion);
        setQuestionNumber(data.questionNumber);
        setTimeRemaining(data.timeRemaining);
        window.scrollTo(0, 0); // Scroll to top when moving to next question
      }

      setSelectedAnswer(null);
      setIsAnswered(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manually submit quiz
  const submitQuizHandler = async () => {
    if (!activeQuizId) {
      setError("Quiz ID not found");
      return;
    }
    if (!window.confirm("Are you sure you want to submit the quiz?")) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/submit-quiz/${activeQuizId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit quiz");
      }

      setResult(data);
      setQuizSubmitted(true);

      // Save score to registration
      if (registrationData.email) {
        try {
          await fetch(`${API_BASE_URL}/api/quiz-registration/update-score`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              quizId: quizIdToUse,
              email: registrationData.email,
              score: data.score,
            }),
          });
        } catch (scoreErr) {
          console.error("Failed to save score:", scoreErr);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch time remaining periodically
  const fetchTimeRemaining = useCallback(async () => {
    if (!activeQuizId || quizSubmitted) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/quiz/time-remaining/${activeQuizId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (data.isTimeExpired && !quizSubmitted) {
        // Time expired, fetch final results
        const resultResponse = await fetch(
          `${API_BASE_URL}/api/quiz/submit-quiz/${activeQuizId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const resultData = await resultResponse.json();
        setResult(resultData);
        setQuizSubmitted(true);
      } else {
        setTimeRemaining(data.timeRemaining);
      }
    } catch (err) {
      console.error("Error fetching time:", err);
    }
  }, [quiz, quizSubmitted]);

  // Timer effect - countdown locally
  useEffect(() => {
    if (!quizStarted || quizSubmitted || !timeRemaining) return;

    // Update timer every second
    const timerInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - submit quiz
          submitQuizHandler();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, [quizStarted, quizSubmitted, timeRemaining]);

  // Prevent text selection, right-click, keyboard shortcuts, and screenshots
  useEffect(() => {
    if (!quizStarted || quizSubmitted) return;

    const preventCopy = (e) => {
      e.preventDefault();
      return false;
    };

    const preventKeyDown = (e) => {
      // Prevent ALL Ctrl combinations except minimal essential ones
      if (e.ctrlKey) {
        e.preventDefault();
        return false;
      }
      // Prevent F1-F12 function keys
      if (e.key.startsWith("F") && e.key.length >= 2) {
        e.preventDefault();
        return false;
      }
      // BLOCK PRINT SCREEN AND ALL SCREENSHOT SHORTCUTS
      if (
        e.key === "PrintScreen" ||
        e.key === "PrtSc" ||
        e.key === "PrtScn" ||
        e.key === "Snapshot" ||
        (e.altKey && e.key === "PrintScreen") ||
        (e.ctrlKey && e.key === "PrintScreen") ||
        (e.winKey && e.key === "PrintScreen") ||
        (e.altKey && e.key === "Tab") ||
        (e.ctrlKey && e.altKey && e.key === "Delete")
      ) {
        e.preventDefault();
        // Silently block - no warning shown
        return false;
      }
      // Block Windows key
      if (e.key === "Meta" || e.key === "OS") {
        e.preventDefault();
        return false;
      }
    };

    // Block context menu
    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Block selection
    const preventSelection = (e) => {
      e.preventDefault();
      return false;
    };

    // Clear clipboard on focus loss
    const handleBlur = () => {
      // Silently clear clipboard when window loses focus
      if (navigator.clipboard) {
        navigator.clipboard.writeText("").catch(() => {});
      }
    };

    document.addEventListener("contextmenu", preventContextMenu);
    document.addEventListener("selectstart", preventSelection);
    document.addEventListener("copy", preventCopy);
    document.addEventListener("cut", preventCopy);
    document.addEventListener("paste", preventCopy);
    document.addEventListener("keydown", preventKeyDown);
    window.addEventListener("blur", handleBlur);

    // Add security classes to body
    document.body.classList.add(
      "quiz-no-select",
      "quiz-no-capture",
      "quiz-secure-mode",
    );

    return () => {
      document.removeEventListener("contextmenu", preventContextMenu);
      document.removeEventListener("selectstart", preventSelection);
      document.removeEventListener("copy", preventCopy);
      document.removeEventListener("cut", preventCopy);
      document.removeEventListener("paste", preventCopy);
      document.removeEventListener("keydown", preventKeyDown);
      window.removeEventListener("blur", handleBlur);
      document.body.classList.remove(
        "quiz-no-select",
        "quiz-no-capture",
        "quiz-secure-mode",
      );
    };
  }, [quizStarted, quizSubmitted]);

  // Handle option selection
  const handleOptionSelect = (option) => {
    if (isAnswered) return;
    setSelectedAnswer(option);
  };

  // Render registration form
  if (!isRegistered) {
    return (
      <div className="quiz-container">
        <div className="quiz-registration-screen">
          <h1>Quiz Registration</h1>
          <p className="registration-subtitle">
            Fill in your details to start the quiz
          </p>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={registerHandler} className="registration-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={registrationData.name}
                  onChange={handleRegistrationChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={registrationData.email}
                  onChange={handleRegistrationChange}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={registrationData.phone}
                  onChange={handleRegistrationChange}
                  placeholder="Enter your phone number"
                  pattern="[0-9]{10}"
                  title="Please enter a valid 10-digit phone number"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="class">Class/Year *</label>
                <input
                  type="text"
                  id="class"
                  name="class"
                  value={registrationData.class}
                  onChange={handleRegistrationChange}
                  placeholder="e.g., TY BSc, SY BCom, etc."
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="college">College Name *</label>
              <input
                type="text"
                id="college"
                name="college"
                value={registrationData.college}
                onChange={handleRegistrationChange}
                placeholder="Enter your college name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">Location/City *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={registrationData.location}
                onChange={handleRegistrationChange}
                placeholder="Enter your city/location"
                required
              />
            </div>

            <div className="registration-info">
              <p>
                ⏱️ <strong>Quiz Duration:</strong> 10 minutes
              </p>
              <p>
                📝 <strong>Total Questions:</strong> 10
              </p>
              <p>
                ✅ <strong>Format:</strong> One question at a time
              </p>
            </div>

            <button
              type="submit"
              className="start-btn"
              disabled={registrationLoading}
            >
              {registrationLoading
                ? "Registering..."
                : "Register & Start Quiz →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Render start screen
  if (!quizStarted) {
    return (
      <div className="quiz-container">
        <div className="quiz-start-screen">
          <h1>Quiz Instructions</h1>
          <div className="instructions">
            <p>
              ⏱️ <strong>Duration:</strong> 10 minutes
            </p>
            <p>
              📝 <strong>Questions:</strong> {totalQuestions || 10} questions
            </p>
            <p>
              ✅ <strong>Format:</strong> One question at a time
            </p>
            <p>
              🚫 <strong>Rules:</strong>
            </p>
            <ul>
              <li>You cannot go back to previous questions</li>
              <li>Text selection and copying is disabled</li>
              <li>Quiz will auto-submit after 10 minutes</li>
            </ul>
          </div>
          {error && <div className="error-message">{error}</div>}
          <button
            className="start-btn"
            onClick={startQuizHandler}
            disabled={loading}
          >
            {loading ? "Starting..." : "Start Quiz"}
          </button>
        </div>
      </div>
    );
  }

  // Render result screen
  if (quizSubmitted && result) {
    return (
      <div className="quiz-container">
        <div className="quiz-result-screen">
          <h1>Quiz Submitted!</h1>

          <div className={`score-card ${getScoreStyle(result.scoreLevel)}`}>
            <div className="score-circle">
              <span className="score">{result.score}</span>
              <span className="total">/ {result.totalQuestions}</span>
            </div>
            <div className="score-percentage">{result.percentage}%</div>
          </div>

          <div className="score-message-box">
            <p className="result-message">{result.scoreMessage}</p>
          </div>

          <div className="stats-grid">
            <div className="stat-item correct">
              <span className="stat-value">{result.correctCount}</span>
              <span className="stat-label">Correct</span>
            </div>
            <div className="stat-item wrong">
              <span className="stat-value">{result.wrongCount}</span>
              <span className="stat-label">Wrong</span>
            </div>
            <div className="stat-item unanswered">
              <span className="stat-value">{result.unansweredCount}</span>
              <span className="stat-label">Unanswered</span>
            </div>
            <div className="stat-item attempted">
              <span className="stat-value">{result.attemptedCount}</span>
              <span className="stat-label">Attempted</span>
            </div>
          </div>

          {result.isTimeExpired && (
            <div className="time-expired-notice">
              ⏰ Time's up! Quiz was auto-submitted.
            </div>
          )}

          <div className="submitted-at">
            Submitted at: {new Date(result.submittedAt).toLocaleString()}
          </div>
        </div>
      </div>
    );
  }

  // Render quiz question
  return (
    <div className="quiz-container">
      {/* Tab Warning Popup */}
      {showTabWarning && (
        <div className="tab-warning-overlay">
          <div className="tab-warning-box">
            <div className="tab-warning-icon">⚠️</div>
            <h2>Warning: Tab Switching Detected!</h2>
            <p>
              This is your <strong>first warning</strong>.
            </p>
            <p>
              If you switch tabs again, your quiz will be{" "}
              <strong>automatically submitted</strong>.
            </p>
            <p className="tab-warning-note">
              Please stay on this page until you complete the quiz.
            </p>
            <button
              className="warning-ok-btn"
              onClick={() => setShowTabWarning(false)}
            >
              I Understand
            </button>
          </div>
        </div>
      )}

      {/* Dynamic Watermark Overlay - Generic security watermark */}
      {quizStarted && !quizSubmitted && (
        <div className="quiz-watermark-overlay">
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="watermark-text">
              SECURE QUIZ - {new Date().toLocaleDateString()}
            </div>
          ))}
        </div>
      )}

      <div className="quiz-header">
        <div className="quiz-info">
          <span className="question-counter">
            Question {questionNumber} of {totalQuestions}
          </span>
        </div>
        <div className={`timer ${timeRemaining < 60 ? "timer-warning" : ""}`}>
          ⏱️ {formatTime(timeRemaining)}
        </div>
        <button
          className="submit-quiz-btn"
          onClick={submitQuizHandler}
          disabled={loading}
        >
          Submit Quiz
        </button>
      </div>

      <div className="quiz-body">
        {error && <div className="error-message">{error}</div>}

        <div className="question-card">
          <h2 className="question-text">{currentQuestion?.statement}</h2>

          <div className="options-list">
            {currentQuestion?.options.map((option, index) => {
              // Get the option value for comparison (handle both string and object options)
              const optionValue =
                typeof option === "object"
                  ? option.option || option.text || option._id
                  : option;
              const isSelected =
                selectedAnswer === optionValue ||
                (typeof selectedAnswer === "object" &&
                  selectedAnswer !== null &&
                  (selectedAnswer.option ||
                    selectedAnswer.text ||
                    selectedAnswer._id) === optionValue);

              return (
                <div
                  key={index}
                  className={`option-item ${isSelected ? "selected" : ""}`}
                  onClick={() => handleOptionSelect(optionValue)}
                >
                  <span className="option-marker">
                    {isSelected ? "🔘" : "⚪"}
                  </span>
                  <span className="option-text">
                    {typeof option === "object"
                      ? option.text || option.option
                      : option}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="quiz-footer">
        <button
          className="next-btn"
          onClick={submitAnswerHandler}
          disabled={!selectedAnswer || loading || isAnswered}
        >
          {loading
            ? "Submitting..."
            : questionNumber === totalQuestions
              ? "Finish Quiz"
              : "Next Question →"}
        </button>
      </div>
    </div>
  );
};

export default QuizPage;
